<script setup lang="ts">
import { ref } from 'vue'
import NewCityList from './CityList.vue'
import NewNetworkList from './NetworkList.vue'
import MobileApps from './MobileApps.vue'
import Socials from './Socials.vue'

defineEmits(['toggleCityFooter', 'toggleNetworkFooter', 'toggleLanguageModal'])

const props = defineProps({
    locale: {
        type: String,
        default: 'en'
    },
    showCitiesFromParent: Boolean,
    showNetworksFromParent: Boolean,
    cdnUrl: {
        type: String,
        default: ''
    }
})

const showCities = ref(false)
const showNetworks = ref(false)

const toggleNetworks = () => {
    showNetworks.value = !showNetworks.value
    showCities.value = showNetworks.value ? false : showCities.value
}

const toggleCities = () => {
    showCities.value = !showCities.value
    showNetworks.value = showCities.value ? false : showNetworks.value
}

const chargemapLogoPath = ref(`${props.cdnUrl}/images/logo-light.svg`)

const chargemapSquareLogoPath = ref(
    `${props.cdnUrl}/images/logo-square-light.svg`
)
</script>

<template>
    <div class="primary-footer">
        <div
            class="pt-2 md:pt-0 gap-4 md:gap-8 lg:gap-16 flex flex-col md:flex-row items-center w-full md:w-auto"
        >
            <a class="block mx-2 md:mx-0" href="/" title="Chargemap">
                <img
                    :src="chargemapLogoPath"
                    class="h-[48px] md:h-11 md:hidden xl:block"
                    alt="Chargemap"
                />
                <img
                    :src="chargemapSquareLogoPath"
                    class="h-10 hidden md:block xl:hidden"
                    alt="Chargemap"
                />
            </a>
            <MobileApps class="block md:hidden" :locale :cdnUrl />

            <div
                class="pb-4 border-b-[1px] border-zinc-200 w-full flex justify-center md:hidden"
            >
                <ChargemapB2CButton
                    type="primary"
                    size="large"
                    class="w-full"
                    custom-classes-for-base-button="justify-between w-full"
                    @click="$emit('toggleLanguageModal')"
                >
                    <template #icon>
                        <IconWorld class="size-4" />
                    </template>
                    <div>
                        <span class="font-normal">
                            {{ $t('word.language_with_colon') }}
                        </span>
                        <span class="uppercase font-bold ml-2">
                            {{ locale }}
                        </span>
                    </div>
                    <IconChevronDown class="size-4" />
                </ChargemapB2CButton>
            </div>

            <div
                class="flex flex-col md:flex-row md:gap-4 lg:gap-16 xl:gap-32 divide-y-[1px] divide-zinc-200 md:divide-y-0 w-full md:w-auto"
            >
                <div class="flex">
                    <a
                        class="accordion-button hidden md:flex flex-row gap-2 items-center"
                        href="#"
                        :aria-expanded="showCitiesFromParent"
                        @click.prevent="$emit('toggleCityFooter')"
                    >
                        {{ $t('includes.footer.chargepoints_by_cities') }}
                        <IconChevronDown
                            class="size-4 transition-transform duration-200"
                        />
                    </a>

                    <div class="mobile-footer-content">
                        <div class="pb-4">
                            <a
                                class="accordion-button w-full flex flex-row justify-between"
                                href="#"
                                :aria-expanded="showCities"
                                @click="toggleCities"
                            >
                                {{
                                    $t('includes.footer.chargepoints_by_cities')
                                }}
                                <IconChevronDown
                                    class="size-7 transition-transform duration-200"
                                    :class="showCities && 'rotate-180'"
                                />
                            </a>
                        </div>
                        <div class="list-content">
                            <NewCityList v-if="showCities" />
                        </div>
                    </div>
                </div>

                <div>
                    <a
                        class="accordion-button hidden md:flex flex-row gap-2 items-center"
                        href="#"
                        :aria-expanded="showNetworksFromParent"
                        @click.prevent="$emit('toggleNetworkFooter')"
                    >
                        {{ $t('includes.footer.chargepoints_by_networks') }}
                        <IconChevronDown
                            class="size-4 transition-transform duration-200"
                        />
                    </a>

                    <div class="mobile-footer-content">
                        <div class="py-4">
                            <a
                                class="accordion-button w-full flex flex-row justify-between"
                                href="#"
                                :aria-expanded="showNetworks"
                                @click="toggleNetworks"
                            >
                                {{
                                    $t(
                                        'includes.footer.chargepoints_by_networks'
                                    )
                                }}
                                <IconChevronDown
                                    class="size-7 transition-transform duration-200"
                                    :class="showNetworks && 'rotate-180'"
                                />
                            </a>
                        </div>
                        <div class="list-content">
                            <NewNetworkList v-if="showNetworks" />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="hidden md:flex gap-8 md:gap-6 lg:gap-16">
            <Socials :locale />
            <MobileApps :locale :cdnUrl />
        </div>
    </div>
</template>

<style scoped>
.primary-footer {
  @apply bg-transparent flex flex-wrap justify-between px-4 md:px-6 lg:px-[50px] md:py-4 items-center;
}
.primary-footer > div {
  @apply self-center;
}
.primary-footer .accordion-button {
  @apply text-sky-700 hover:text-sky-500 active:text-sky-700 text-[15px] md:text-[13px] lg:text-[15px] font-bold focus:no-underline transition-all duration-300;
}
.primary-footer .accordion-button[aria-expanded=true] svg {
  @apply rotate-180 active:no-underline focus:no-underline;
}
.primary-footer .mobile-footer-content {
  @apply flex md:hidden flex-col md:gap-20 w-full;
}
.primary-footer .mobile-footer-content .list-content {
  @apply overflow-hidden transition-all duration-300;
}
.primary-footer .mobile-footer-content .list-content ul {
  @apply md:px-6;
}
</style>
